'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Shield, Copy, Check, ChevronRight, ArrowLeft } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useGlobalStore } from '@/store/useGlobalStore';
import { motion, AnimatePresence } from 'framer-motion';
import I18nProvider from '@/components/I18nProvider';
import ToastManager from '@/components/ToastManager';
import appConfig from '@/config/appConfig';
import api, { fetchDesignAndWS, applyDesignFavicon, getCurrency } from '@/utils/api';
import { DEMO_CREDENTIALS } from '@/mocks/auth';
import { useTranslation } from 'react-i18next';


interface LoginFormData {
  identifier: string;
  password: string;
}

interface TwoFAFormData {
  code: string;
}

interface DesignConfig {
  logo?: string;
  title?: string;
  favicon?: string;
}

const LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'zh', label: '中文', flag: '🇨🇳' },
  { value: 'ar', label: 'العربية', flag: '🇸🇦' },
  { value: 'he', label: 'עברית', flag: '🇮🇱' },
];

export default function LoginScreen() {
  const router = useRouter();
  const { login, addToast, setLanguage, language } = useGlobalStore();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [show2FA, setShow2FA] = useState(false);
  // Store pending login state for 2FA
  const [pending2FAData, setPending2FAData] = useState<{
    identifier: string;
    password: string;
    password_type: string;
    type: string;
  } | null>(null);
  const [is2FALoading, setIs2FALoading] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [designConfig, setDesignConfig] = useState<DesignConfig>(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('design') : null;
      if (raw) {
        const d = JSON.parse(raw);
        const logo = (d?.logo_dark as string) || (d?.logo as string) || '';
        applyDesignFavicon(d?.favicon as string);
        return { logo, title: d?.title || d?.company_name || '', favicon: d?.favicon || '' };
      }
    } catch { /* ignore */ }
    return {};
  });

  const loginForm = useForm<LoginFormData>();
  const twoFAForm = useForm<TwoFAFormData>();

  // Fetch design config on mount (no auth headers on login page)
  useEffect(() => {
    fetchDesignAndWS()
      .then(() => {
        try {
          const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('design') : null;
          if (raw) {
            const design = JSON.parse(raw);
            applyDesignFavicon(design?.favicon as string);
            setDesignConfig({
              logo: (design?.logo_dark as string) || (design?.logo as string) || '',
              title: design?.title || design?.company_name || '',
              favicon: design?.favicon || '',
            });
          }
        } catch {
          // ignore
        }
      })
      .catch(() => {});
  }, []);

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = (email: string, password: string) => {
    loginForm.setValue('identifier', email);
    loginForm.setValue('password', password);
  };

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      // POST auth/login with identifier + password
      const res = await api.post('auth/login', {
        identifier: data.identifier,
        password: data.password,
      });
      const resData = res.data;
      const code = resData?.code ?? resData?.status;

      if (code === 301) {
        // 2FA required
        setPending2FAData({
          identifier: data.identifier,
          password: data.password,
          password_type: resData?.password_type || 'normal',
          type: resData?.type || 'client',
        });
        setShow2FA(true);
      } else if (code === 200 || resData?.access_token || resData?.result?.access_token) {
        const result = resData?.result || resData;
        const accessToken: string = result?.access_token || result?.token || '';
        const accountId: string = result?.account_id || result?.accounts?.[0]?.account_id || '';
        const accounts = result?.accounts || [];
        const userInfo = result?.user_info || result?.user || {};

        // Save to localStorage as per API doc
        if (typeof localStorage !== 'undefined') {
          if (accessToken) localStorage.setItem('access_token', accessToken);
          if (accountId) localStorage.setItem('account_id', accountId);
          if (result?.type) localStorage.setItem('type', result.type);
          if (accounts.length) localStorage.setItem('accounts', JSON.stringify(accounts));
          if (userInfo) localStorage.setItem('user_info', JSON.stringify(userInfo));
          if (result?.['app-theme']) localStorage.setItem('app-theme', result['app-theme']);
          if (result?.['traderhub-language']) localStorage.setItem('traderhub-language', result['traderhub-language']);
        }

        // Map to global store format
        const mappedUser = {
          id: userInfo?.id || accountId,
          name: userInfo?.name || userInfo?.fullname || 'Trader',
          email: userInfo?.email || data.identifier,
          phone: userInfo?.phone || '',
          country: userInfo?.country || '',
          kycStatus: (userInfo?.kyc_status || 'not_submitted') as 'not_submitted' | 'pending' | 'verified' | 'rejected',
          twoFAEnabled: userInfo?.two_factor || false,
          avatar: userInfo?.avatar || '',
        };

        const mappedAccounts = Array.isArray(accounts) ? accounts.map((a: Record<string, unknown>) => ({
          id: String(a.accountNumber || a.id || a.account_id || ''),
          account_id: String(a.accountNumber || a.account_id || a.id || ''),
          login: String(a.accountNumber || a.login || a.account_id || ''),
          name: String(a.name || ''),
          type: (a.type === 'demo' ? 'demo' : 'live') as 'live' | 'demo',
          currency: String(a.currency || getCurrency()),
          balance: Number(a.balance || 0),
          equity: Number(a.equity || 0),
          margin: Number(a.margin || 0),
          freeMargin: Number(a.free_margin || a.freeMargin || 0),
          marginLevel: Number(a.margin_level || a.marginLevel || 0),
          leverage: String(a.leverage || '1:100'),
          server: String(a.server || ''),
          isActive: Boolean(a.is_active || a.isActive || false),
          wt_url: a.wt_url ? String(a.wt_url) : undefined,
          wt_redirect: a.wt_redirect ? String(a.wt_redirect) : undefined,
        })) : [];

        login(mappedUser, accessToken, mappedAccounts);
        addToast({ type: 'success', message: `Welcome back, ${mappedUser.name.split(' ')[0]}!` });
        router.push('/');
      } else {
        addToast({ type: 'error', message: resData?.message || 'Login failed. Please check your credentials.' });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      addToast({ type: 'error', message: axiosErr?.response?.data?.message || 'Login failed. Please check your credentials.' });
    } finally {
      setIsLoading(false);
    }
  };

  const on2FASubmit = async (data: TwoFAFormData) => {
    if (!pending2FAData) return;
    setIs2FALoading(true);
    try {
      // POST auth/verify-2fa
      const res = await api.post('auth/verify-2fa', {
        identifier: pending2FAData.identifier,
        password: pending2FAData.password,
        password_type: pending2FAData.password_type,
        type: pending2FAData.type,
        verify_code: data.code,
      });
      const resData = res.data;
      const result = resData?.result || resData;
      const code = resData?.code ?? resData?.status;

      if (code === 200 || result?.access_token) {
        const accessToken: string = result?.access_token || result?.token || '';
        const accountId: string = result?.account_id || result?.accounts?.[0]?.account_id || '';
        const accounts = result?.accounts || [];
        const userInfo = result?.user_info || result?.user || {};

        if (typeof localStorage !== 'undefined') {
          if (accessToken) localStorage.setItem('access_token', accessToken);
          if (accountId) localStorage.setItem('account_id', accountId);
          if (result?.type) localStorage.setItem('type', result.type);
          if (accounts.length) localStorage.setItem('accounts', JSON.stringify(accounts));
          if (userInfo) localStorage.setItem('user_info', JSON.stringify(userInfo));
        }

        const mappedUser = {
          id: userInfo?.id || accountId,
          name: userInfo?.name || userInfo?.fullname || 'Trader',
          email: userInfo?.email || pending2FAData.identifier,
          phone: userInfo?.phone || '',
          country: userInfo?.country || '',
          kycStatus: (userInfo?.kyc_status || 'not_submitted') as 'not_submitted' | 'pending' | 'verified' | 'rejected',
          twoFAEnabled: true,
          avatar: userInfo?.avatar || '',
        };

        const mappedAccounts = Array.isArray(accounts) ? accounts.map((a: Record<string, unknown>) => ({
          id: String(a.accountNumber || a.id || a.account_id || ''),
          account_id: String(a.accountNumber || a.account_id || a.id || ''),
          login: String(a.accountNumber || a.login || a.account_id || ''),
          name: String(a.name || ''),
          type: (a.type === 'demo' ? 'demo' : 'live') as 'live' | 'demo',
          currency: String(a.currency || getCurrency()),
          balance: Number(a.balance || 0),
          equity: Number(a.equity || 0),
          margin: Number(a.margin || 0),
          freeMargin: Number(a.free_margin || a.freeMargin || 0),
          marginLevel: Number(a.margin_level || a.marginLevel || 0),
          leverage: String(a.leverage || '1:100'),
          server: String(a.server || ''),
          isActive: Boolean(a.is_active || a.isActive || false),
          wt_url: a.wt_url ? String(a.wt_url) : undefined,
          wt_redirect: a.wt_redirect ? String(a.wt_redirect) : undefined,
        })) : [];

        login(mappedUser, accessToken, mappedAccounts);
        addToast({ type: 'success', message: `${t('welcomeBack')}, ${mappedUser.name.split(' ')[0]}!` });
        router.push('/');
      } else {
        twoFAForm.setError('code', { message: resData?.message || 'Invalid verification code' });
      }
    } catch {
      twoFAForm.setError('code', { message: 'Verification failed. Please try again.' });
    } finally {
      setIs2FALoading(false);
    }
  };

  const currentLang = LANGUAGES.find((l) => l.value === language);
  const appTitle = designConfig.title;

  return (
    <I18nProvider>
      <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ background: 'var(--background)' }}>
        {/* Background gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% -10%, rgba(245,158,11,0.12) 0%, transparent 60%)' }}
        />

        {/* Top bar */}
        <div className="flex items-center justify-end px-5 pt-12 pb-4 relative" style={{ zIndex: 300 }}>
          <div className="relative">
            <button
              onClick={() => setShowLangPicker(!showLangPicker)}
              className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-95"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              aria-label={t('language')}
            >
              <span className="text-xl leading-none">{currentLang?.flag || '🌐'}</span>
            </button>

            <AnimatePresence>
              {showLangPicker && (
                <>
                  <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => setShowLangPicker(false)} />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: -8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -8 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute top-full right-0 mt-2 rounded-2xl overflow-hidden shadow-2xl"
                    style={{ background: 'var(--card)', border: '1px solid var(--border)', zIndex: 9999, minWidth: 180, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}
                  >
                    {LANGUAGES.map((lang, idx) => (
                      <button
                        key={lang.value}
                        onClick={() => { setLanguage(lang.value); setShowLangPicker(false); }}
                        className="w-full px-4 py-3 flex items-center gap-3 transition-colors"
                        style={{
                          background: language === lang.value ? 'rgba(245,158,11,0.1)' : 'transparent',
                          borderBottom: idx < LANGUAGES.length - 1 ? '1px solid var(--border)' : 'none',
                        }}
                      >
                        <span className="text-xl leading-none">{lang.flag}</span>
                        <span className="text-sm font-medium flex-1 text-left" style={{ color: language === lang.value ? 'var(--primary)' : 'var(--foreground)' }}>
                          {lang.label}
                        </span>
                        {language === lang.value && <Check size={13} style={{ color: 'var(--primary)' }} />}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 px-5 pb-8 relative z-10 overflow-y-auto scrollbar-hide">
          <AnimatePresence mode="wait">
            {/* ── 2FA SCREEN ── */}
            {show2FA ? (
              <motion.div
                key="screen-2fa"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.25 }}
              >
                <div className="mb-8">
                  <button
                    onClick={() => { setShow2FA(false); setPending2FAData(null); }}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4 transition-all active:scale-95"
                  >
                    <ArrowLeft size={14} />
                    {t('backToLoginLink')}
                  </button>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--primary)' }}>
                    <Shield size={26} className="text-black" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground mb-1">{t('twoFactorAuthTitle')}</h1>
                  <p className="text-sm text-muted-foreground">{t('enterSixDigitCode')}</p>
                </div>

                <form onSubmit={twoFAForm.handleSubmit(on2FASubmit)} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('authenticatorCode')}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      className="w-full px-4 py-4 rounded-2xl text-xl text-center font-mono tracking-[0.6em] text-foreground placeholder-muted-foreground outline-none transition-all"
                      style={{
                        background: 'var(--input)',
                        border: `1px solid ${twoFAForm.formState.errors.code ? 'var(--negative)' : 'var(--border)'}`,
                        letterSpacing: '0.5em',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                      onBlur={(e) => (e.target.style.borderColor = twoFAForm.formState.errors.code ? 'var(--negative)' : 'var(--border)')}
                      {...twoFAForm.register('code', {
                        required: t('codeRequired'),
                        minLength: { value: 6, message: 'Enter 6-digit code' },
                        maxLength: { value: 6, message: 'Enter 6-digit code' },
                        pattern: { value: /^\d{6}$/, message: 'Numbers only' },
                      })}
                    />
                    {twoFAForm.formState.errors.code && (
                      <p className="text-negative text-xs mt-1.5">{twoFAForm.formState.errors.code.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={is2FALoading}
                    className="w-full py-4 rounded-2xl text-sm font-bold text-primary-foreground transition-all active:scale-95 flex items-center justify-center gap-2 mt-2"
                    style={{ background: is2FALoading ? 'rgba(245,158,11,0.6)' : 'var(--primary)', boxShadow: is2FALoading ? 'none' : '0 4px 20px rgba(245,158,11,0.3)' }}
                  >
                    {is2FALoading ? (
                      <>
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full" />
                        {t('verifying')}
                      </>
                    ) : (
                      <>{t('verifyCode')} <ChevronRight size={16} /></>
                    )}
                  </button>
                </form>

                <p className="text-xs text-muted-foreground text-center mt-6">
                  {t('openAuthApp')}
                </p>
              </motion.div>
            ) : (
              /* ── LOGIN SCREEN ── */
              <motion.div
                key="screen-login"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.25 }}
              >
                {/* Company Logo */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="flex flex-col items-center mb-8 mt-2"
                >
                  {designConfig.logo ? (
                    <div className="w-20 h-20 rounded-3xl overflow-hidden mb-3 flex items-center justify-center" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                      <img src={designConfig.logo} alt={`${appTitle} logo`} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-3" style={{ background: 'var(--primary)' }}>
                      <span className="text-3xl font-black text-black">{appTitle ? appTitle.charAt(0).toUpperCase() : '◆'}</span>
                    </div>
                  )}
                  <h1 className="text-2xl font-bold text-foreground tracking-tight">{appTitle}</h1>
                  <p className="text-sm text-muted-foreground mt-1">{t('signInToAccount')}</p>
                </motion.div>

                {/* Form */}
                <motion.form
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                  className="space-y-4"
                >
                  {/* Identifier (email or ID) */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('emailOrAccountId')}</label>
                    <input
                      type="text"
                      autoComplete="username"
                      placeholder={t('emailPlaceholder')}
                      className="w-full px-4 py-3.5 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                      style={{ background: 'var(--input)', border: `1px solid ${loginForm.formState.errors.identifier ? 'var(--negative)' : 'var(--border)'}` }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                      onBlur={(e) => (e.target.style.borderColor = loginForm.formState.errors.identifier ? 'var(--negative)' : 'var(--border)')}
                      {...loginForm.register('identifier', { required: t('emailOrAccountId') + ' is required' })}
                    />
                    {loginForm.formState.errors.identifier && (
                      <p className="text-negative text-xs mt-1.5">{loginForm.formState.errors.identifier.message}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-muted-foreground">{t('password')}</label>
                      <button type="button" onClick={() => router.push('/forgot-password')} className="text-xs font-medium transition-colors" style={{ color: 'var(--primary)' }}>
                        {t('forgotPassword')}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder={t('enterYourPassword')}
                        className="w-full px-4 py-3.5 pr-12 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                        style={{ background: 'var(--input)', border: `1px solid ${loginForm.formState.errors.password ? 'var(--negative)' : 'var(--border)'}` }}
                        onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                        onBlur={(e) => (e.target.style.borderColor = loginForm.formState.errors.password ? 'var(--negative)' : 'var(--border)')}
                        {...loginForm.register('password', { required: t('password') + ' is required' })}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-negative text-xs mt-1.5">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-4 rounded-2xl text-sm font-bold text-primary-foreground transition-all active:scale-95 flex items-center justify-center gap-2 mt-2"
                    style={{ background: isLoading ? 'rgba(245,158,11,0.6)' : 'var(--primary)', boxShadow: isLoading ? 'none' : '0 4px 20px rgba(245,158,11,0.3)' }}
                  >
                    {isLoading ? (
                      <>
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full" />
                        {t('signingIn')}
                      </>
                    ) : (
                      <>{t('signIn')} <ChevronRight size={16} /></>
                    )}
                  </button>
                </motion.form>

                {/* Register link */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-5 text-center">
                  <span className="text-sm text-muted-foreground">{t('dontHaveAccount')} </span>
                  <button type="button" onClick={() => router.push('/register')} className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>
                    {t('register')}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <ToastManager />
      </div>
    </I18nProvider>
  );
}